/**
 * Network Status Hook for PH Tablet POS
 * Provides online/offline detection with sync integration
 */
import { useState, useEffect, useCallback } from 'react';
import { getOfflineSalesCount } from './offlineStore';
import { subscribeSyncStatus, startSync, forceSync, getSyncingStatus } from './syncWorker';

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineCount, setOfflineCount] = useState({ pending: 0, failed: 0 });
  const [syncStatus, setSyncStatus] = useState({ type: 'IDLE' });

  // Update offline sales count
  const updateOfflineCount = useCallback(async () => {
    try {
      const count = await getOfflineSalesCount();
      setOfflineCount(count);
    } catch (error) {
      console.error('Failed to get offline count:', error);
    }
  }, []);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Subscribe to sync status
  useEffect(() => {
    const unsubscribe = subscribeSyncStatus((status) => {
      setSyncStatus(status);
      if (status.type === 'SYNC_COMPLETE') {
        updateOfflineCount();
      }
    });

    return unsubscribe;
  }, [updateOfflineCount]);

  // Initial count load
  useEffect(() => {
    updateOfflineCount();
    
    // Poll every 30 seconds
    const interval = setInterval(updateOfflineCount, 30000);
    return () => clearInterval(interval);
  }, [updateOfflineCount]);

  return {
    isOnline,
    offlineCount,
    syncStatus,
    updateOfflineCount,
    isSyncing: getSyncingStatus().isSyncing || syncStatus.type === 'SYNCING' || syncStatus.type === 'SYNC_START'
  };
}

/**
 * Hook for triggering sync operations
 */
export function useSyncTrigger() {
  const triggerSync = useCallback(async (token) => {
    if (navigator.onLine && token) {
      await startSync(token);
    }
  }, []);

  const triggerForceSync = useCallback(async (token) => {
    if (navigator.onLine && token) {
      await forceSync(token);
    }
  }, []);

  return {
    triggerSync,
    triggerForceSync
  };
}

export default useNetworkStatus;
